// Resolution logic for the JSON order-import flow (see OrderImportDialog).
// Pure functions only, no React — turns the loosely-typed external JSON
// (OrderImportPayload) into real products/customers/channels using the
// exact same search endpoints staff already use, so match quality here is
// never worse than a human typing the same term into the live search
// boxes.
import { Product, Customer } from "../types";
import {
    OrderImportCustomer,
    OrderImportItem,
    OrderImportPayload,
    OrderImportStatus,
} from "../types/orderImport";
import { productService } from "../services/productService";
import { customerService } from "../services/customerService";
import { tryNormalizeBdPhone } from "./phone";
import { PaymentMethod, PAYMENT_METHOD_LABELS } from "./paymentMethods";

export type ProductResolution =
    | { status: "matched"; product: Product }
    | { status: "ambiguous"; candidates: Product[] }
    | { status: "unresolved" };

export type CustomerResolution =
    | { status: "matched"; customer: Customer }
    | { status: "not_found" };

const IMPORTABLE_STATUSES: OrderImportStatus[] = ["DRAFT", "PENDING", "CONFIRMED"];

const isBlank = (v: unknown) => v === undefined || v === null || v === "";

// LLMs regularly emit numbers as strings ("80", "৳80", "1,200", "80 tk").
// Accept those instead of silently dropping the value. Everything but
// digits and the decimal point is stripped, so a minus sign can't survive —
// none of these fields (money, quantity) can legitimately be negative.
const toNumber = (v: unknown): number | undefined => {
    if (typeof v === "number") return Number.isFinite(v) ? v : undefined;
    if (typeof v === "string") {
        const cleaned = v.replace(/[^0-9.]/g, "");
        if (!cleaned || cleaned === ".") return undefined;
        const n = Number(cleaned);
        return Number.isFinite(n) ? n : undefined;
    }
    return undefined;
};

const toStr = (v: unknown): string | undefined => {
    if (typeof v === "string") {
        const t = v.trim();
        return t || undefined;
    }
    if (typeof v === "number" && Number.isFinite(v)) return String(v);
    return undefined;
};

const toBool = (v: unknown): boolean | undefined => {
    if (typeof v === "boolean") return v;
    if (typeof v === "string") {
        const t = v.trim().toLowerCase();
        if (["true", "yes", "y", "1"].includes(t)) return true;
        if (["false", "no", "n", "0"].includes(t)) return false;
    }
    return undefined;
};

export function parseOrderImportPayload(
    raw: string,
): { payload: OrderImportPayload; warnings: string[] } | { error: string } {
    let json: any;
    try {
        json = JSON.parse(raw);
    } catch {
        return {
            error: "That doesn't look like valid JSON — check for a missing bracket, brace, or comma.",
        };
    }
    if (!json || typeof json !== "object" || Array.isArray(json)) {
        return { error: "Expected a JSON object at the top level." };
    }
    if (!Array.isArray(json.items) || json.items.length === 0) {
        return { error: 'The payload needs a non-empty "items" array.' };
    }
    for (let i = 0; i < json.items.length; i++) {
        const item = json.items[i];
        if (!item || typeof item !== "object" || (!item.sku && !item.product_name)) {
            return {
                error: `Item ${i + 1} needs at least a "sku" or "product_name".`,
            };
        }
    }

    const warnings: string[] = [];

    // A provided-but-unusable value is surfaced on the review screen rather
    // than silently dropped, so staff know to fill that field in by hand.
    const money = (key: string, value: unknown): number | undefined => {
        if (isBlank(value)) return undefined;
        const n = toNumber(value);
        if (n === undefined) {
            warnings.push(`Ignored "${key}": "${String(value)}" isn't a usable amount.`);
        }
        return n;
    };

    const c = json.customer && typeof json.customer === "object" ? json.customer : {};
    const customer: OrderImportCustomer = {
        phone: toStr(c.phone),
        first_name: toStr(c.first_name),
        last_name: toStr(c.last_name),
        email: toStr(c.email),
        alternative_phone: toStr(c.alternative_phone),
        street_address: toStr(c.street_address),
        city: toStr(c.city),
        map_link: toStr(c.map_link),
        profile: toStr(c.profile),
        note: toStr(c.note),
    };

    const da =
        json.delivery_address && typeof json.delivery_address === "object"
            ? json.delivery_address
            : undefined;
    const delivery_address =
        da && (toStr(da.street_address) || toStr(da.city))
            ? { street_address: toStr(da.street_address), city: toStr(da.city) }
            : undefined;

    let status: OrderImportStatus | undefined;
    const rawStatus = toStr(json.status)?.toUpperCase();
    if (rawStatus) {
        if ((IMPORTABLE_STATUSES as string[]).includes(rawStatus)) {
            status = rawStatus as OrderImportStatus;
        } else {
            warnings.push(
                `Ignored status "${json.status}" — only DRAFT, PENDING or CONFIRMED can be imported. Set anything else in the form.`,
            );
        }
    }

    const items: OrderImportItem[] = json.items.map((item: any) => {
        const quantity = toNumber(item.quantity);
        return {
            sku: toStr(item.sku),
            product_name: toStr(item.product_name),
            customer_hint: toStr(item.customer_hint),
            quantity: quantity && quantity > 0 ? Math.floor(quantity) || 1 : undefined,
            unit_price: toNumber(item.unit_price),
            is_gift: toBool(item.is_gift),
            needs_review: toBool(item.needs_review),
            review_note: toStr(item.review_note),
        };
    });

    const payload: OrderImportPayload = {
        customer,
        delivery_address,
        channel: toStr(json.channel),
        delivery_service: toStr(json.delivery_service),
        items,
        delivery_hint: toStr(json.delivery_hint),
        shipping_hint: toStr(json.shipping_hint),
        delivery_charge: money("delivery_charge", json.delivery_charge),
        delivery_cost: money("delivery_cost", json.delivery_cost),
        discount_amount: money("discount_amount", json.discount_amount),
        payment_method: toStr(json.payment_method),
        payment_amount: money("payment_amount", json.payment_amount),
        payment_reference: toStr(json.payment_reference),
        status,
        order_notes: toStr(json.order_notes),
        show_gifts_on_bill: toBool(json.show_gifts_on_bill),
        notes: toStr(json.notes),
        order_needs_review: toBool(json.order_needs_review),
        order_review_note: toStr(json.order_review_note),
    };

    return { payload, warnings };
}

// SKU exact-lookup first (fast path, zero ambiguity), then falls back to
// the same tokenized search ProductSearchAutocomplete itself uses. Never
// invents new matching logic — just sequences the trusted search staff
// already rely on.
export async function resolveProduct(
    item: OrderImportItem,
): Promise<ProductResolution> {
    const skuTerm = item.sku?.trim();
    if (skuTerm) {
        try {
            const product = await productService.getProductBySku(skuTerm);
            return { status: "matched", product };
        } catch (err: any) {
            if (err?.response?.status !== 404) {
                console.error("SKU lookup failed during order import:", err);
            }
            // 404 (or any other failure) just falls through to the
            // tokenized search below, using the SKU itself as the term.
        }
    }

    const searchTerm = skuTerm || item.product_name?.trim();
    if (!searchTerm) return { status: "unresolved" };

    try {
        const response = await productService.getProducts({
            search: searchTerm,
            limit: 5,
        });
        const candidates = response?.data || [];
        if (candidates.length === 1) {
            return { status: "matched", product: candidates[0] };
        }
        if (candidates.length > 1) {
            return { status: "ambiguous", candidates };
        }
        return { status: "unresolved" };
    } catch (err) {
        console.error("Product search failed during order import:", err);
        return { status: "unresolved" };
    }
}

// Mirrors the phone-based matching guest_orders.py's
// _find_or_create_customer already uses server-side — exact normalized
// phone equality, not just "search returned something", since a
// substring/tokenized search can surface a different customer whose phone
// merely contains the same digits.
export async function resolveCustomer(
    customer: OrderImportCustomer | undefined,
): Promise<CustomerResolution> {
    const rawPhone = customer?.phone?.trim();
    if (!rawPhone) return { status: "not_found" };

    const normalized = tryNormalizeBdPhone(rawPhone);
    if (!normalized) return { status: "not_found" };

    try {
        const response = await customerService.getCustomers({
            search: normalized,
            limit: 10,
        });
        const match = (response?.customers || []).find(
            (c) => tryNormalizeBdPhone(c.phone) === normalized,
        );
        return match ? { status: "matched", customer: match } : { status: "not_found" };
    } catch (err) {
        console.error("Customer search failed during order import:", err);
        return { status: "not_found" };
    }
}

const normName = (s: string) => s.trim().toLowerCase().replace(/\s+/g, " ");

// Free-text name -> the matching entry of a short, staff-configured list
// (order channels, delivery services). Case-insensitive exact match first;
// failing that, a single unambiguous partial match ("Pathao" vs "Pathao
// Courier"). Two or more partial hits, or none, resolve to nothing — the
// dropdown is simply left for staff to pick, same as before.
export function resolveNamed<T extends { name: string }>(
    name: string | undefined,
    list: T[],
): T | undefined {
    const term = name ? normName(name) : "";
    if (!term) return undefined;
    const exact = list.find((e) => normName(e.name) === term);
    if (exact) return exact;
    const partial = list.filter((e) => {
        const n = normName(e.name);
        return n.includes(term) || term.includes(n);
    });
    return partial.length === 1 ? partial[0] : undefined;
}

// Only methods that mean "money actually changed hands". COD (paid to the
// courier later) and STORE_CREDIT are deliberately not importable.
const IMPORTABLE_PAYMENT_METHODS: PaymentMethod[] = [
    "CASH",
    "CARD",
    "BKASH",
    "NAGAD",
    "ROCKET",
    "BANK_TRANSFER",
];

const PAYMENT_ALIASES: Record<string, PaymentMethod> = {
    bank: "BANK_TRANSFER",
    debitcard: "CARD",
    creditcard: "CARD",
};

const squash = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");

export function resolvePaymentMethod(
    raw: string | undefined,
): PaymentMethod | undefined {
    const term = raw ? squash(raw) : "";
    if (!term) return undefined;
    const direct = IMPORTABLE_PAYMENT_METHODS.find(
        (m) => squash(m) === term || squash(PAYMENT_METHOD_LABELS[m]) === term,
    );
    return direct ?? PAYMENT_ALIASES[term];
}
